import { IScopeClause } from "@/api";
import { createClauseComment } from "@/api/scopes/scopes";
import { Button, InputAdornment, Stack, Typography } from "@mui/material";
import { FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

interface CommentSectionProps {
    scopeClause: IScopeClause;
}

interface ICommentData {
    comment: string;
}

const CommentSectionFormElements = ({
    isPending,
    reset,
}: {
    isPending: boolean;
    reset: boolean;
}) => {
    const { reset: resetForm, watch } = useFormContext();
    const commentValue = watch("comment");
    useEffect(() => {
        resetForm();
    }, [reset, resetForm]);
    return (
        <>
            <FormTextFieldElement
                disabled={isPending}
                multiline
                name="comment"
                placeholder="Leave comment here"
                sx={{
                    width: "100%",
                }}
                slotProps={{
                    input: {
                        endAdornment: commentValue && (
                            <InputAdornment position="end">
                                <div style={{ alignItems: "end" }}>
                                    <Button
                                        variant="contained"
                                        color="tertiary"
                                        type="submit"
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: "22px",
                                            minWidth: 0,
                                            maxWidth: 40,
                                            fontSize: 10,
                                            fontWeight: 500,
                                        }}
                                        disableElevation
                                    >
                                        Save
                                    </Button>
                                </div>
                            </InputAdornment>
                        ),
                        sx: {
                            alignItems: "end",
                            fontSize: "12px",
                            letterSpacing: "0.15px",
                        },
                    },
                }}
            />
        </>
    );
};

export const CommentForm = ({ scopeClause }: CommentSectionProps) => {
    const queryClient = useQueryClient();
    const [reset, setReset] = useState(false);

    const { mutate, isPending /*isSuccess, isError*/ } = useMutation({
        mutationFn: (comment: string) =>
            createClauseComment(scopeClause.scope_id, scopeClause.id, {
                comment,
            }),

        onSuccess: () => {
            setReset(!reset);
        },

        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: [`frontend-scope-clause-comments`, scopeClause.id],
            });
        },
        mutationKey: ["addComment"],
    });

    const defaultValues: ICommentData = {
        comment: "",
    };

    const handleSubmit = (data: ICommentData) => {
        mutate(data.comment);
    };

    return (
        <Stack spacing={2} justifyContent="space-between">
            <Typography variant="body2" fontWeight={"bold"} gutterBottom>
                Comments
            </Typography>
            <FormContainer
                defaultValues={defaultValues}
                onSuccess={handleSubmit}
            >
                <CommentSectionFormElements
                    isPending={isPending}
                    reset={reset}
                />
            </FormContainer>
        </Stack>
    );
};
